<template>
  <fieldset :class="{ error: error }">
    <h1>{{ $t("auth-form-register-title") }}</h1>
    <div class="form-group">
      <label>
        {{ $t("auth-register-form-email-label") }}
        <p v-if="errorCode" :content="errorCode" class="error-description">
          {{ $t(errorCode) }}
        </p>
        <input
          type="email"
          name="username"
          autocomplete="new-email"
          v-model.trim="username"
          :placeholder="$t('auth-register-form-email-placeholder')"
          @keydown="clearValidation"
        />
      </label>
      <error-icon
        v-if="error"
        fillColor="#ff4d4d"
        width="18"
        height="18"
        cssClass="icon"
      ></error-icon>
    </div>
    <div class="form-group">
      <label>
        {{ $t("auth-register-form-password-label") }}
        <input
          type="password"
          name="password"
          autocomplete="new-password"
          v-model.trim="password"
          :placeholder="$t('auth-register-form-password-placeholder')"
          @keydown="clearValidation"
        />
      </label>
      <div id="meter">
        <div id="password-strength-description">
          <p>{{ $t("auth-form-register-password-strength-title") }}</p>
          <p v-if="strength" :class="strength">
            {{ $t(`auth-form-register-password-strength-${strength}`) }}
          </p>
        </div>
        <div id="password-strength-meter">
          <span :data-length="passwordStrengthMeter"></span>
        </div>
      </div>
    </div>
    <div class="form-group">
      <button v-if="loading" type="submit" class="primary" disabled>
        {{ $t("loading") }}
      </button>
      <button
        v-else
        type="submit"
        class="primary"
        :disabled="!this.username || passwordStrengthMeter < 1 || !formValid"
        @click.prevent="register"
      >
        {{ $t("auth-form-continue") }}
        <long-arrow width="25" height="16" weight="solid"></long-arrow>
      </button>
    </div>
  </fieldset>
</template>

<script>
import { mapState, mapActions } from "vuex";
import zxcvbn from "zxcvbn";
import LongArrow from "@/components/icons/LongArrowIcon";
import ErrorIcon from "@/components/icons/ErrorIcon";

export default {
  name: "RegisterForm",
  components: {
    LongArrow,
    ErrorIcon
  },
  data() {
    return {
      error: null,
      errorCode: null,
      username: "",
      password: ""
    };
  },
  computed: {
    ...mapState("common", ["loading"]),
    passwordStrengthMeter() {
      const strength = zxcvbn(this.password);
      return strength.score;
    },
    strength() {
      let strength = "";
      switch (this.passwordStrengthMeter) {
        case 1:
          strength = "weak";
          break;
        case 2:
          strength = "medium";
          break;
        case 3:
          strength = "strong";
          break;
        case 4:
          strength = "strong";
          break;
      }
      return strength;
    },
    formValid() {
      return this.username.match(/.@./g);
    }
  },
  methods: {
    ...mapActions("common", ["setLoading"]),
    register() {
      if (this.error || this.errorCode) return;
      this.setLoading(true);
      let data = {
        username: this.username,
        password: this.password,
        language: localStorage.getItem("locale") || "en"
      };
      this.$store
        .dispatch("auth/register", data)
        .then(res => {
          if (res && res.status === 200) {
            this.$router.push({ name: "verification" });
          }
          this.setLoading(false);
        })
        .catch(err => {
          this.setLoading(false);
          this.resetForm();
          this.error = err;
          this.errorCode = err.errorCode || "";
        });
    },
    resetForm() {
      this.username = "";
      this.password = "";
    },
    clearValidation() {
      if (!this.error) return;
      this.error = null;
      this.errorCode = null;
      this.resetForm();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/styles/variables";
#meter {
  margin: 10px 0;
}
#password-strength-description {
  display: flex;
  justify-content: space-between;
  > p {
    margin: 0;
    padding: 5px 0;
    color: $placeholder-color;
    &.weak {
      color: $error-color;
    }
    &.medium {
      color: $warning-color;
    }
    &.strong {
      color: $success-color;
    }
  }
}
#password-strength-meter {
  width: 100%;
  height: 12px;
  border-radius: $radius;
  background-color: $middle-gray;
  > span {
    transition: width 0.5s;
    display: block;
    height: 100%;
    border-radius: $radius;
    width: 0;
    &[data-length="1"] {
      width: 33%;
      background-color: $error-color;
    }
    &[data-length="2"] {
      width: 66%;
      background-color: $warning-color;
    }
    &[data-length="3"] {
      width: 100%;
      background-color: $success-color;
    }
    &[data-length="4"] {
      width: 100%;
      background-color: $success-color;
    }
  }
}

.form-group {
  position: relative;
  .icon {
    position: absolute;
    right: 0;
    transform: translateX(25px) translateY(-30px);
  }
}
</style>
